"use client";

import { useState, useCallback } from "react";
import VirtuosoMainCard from "@/components/virtuosomaincard/VirtuosoMainCardTags";
import { GenreContentItem } from "@/types/tag";
import { HomeBlockDetailItem, HomeBlockDetailData } from "@/types/home";
import { getHomeBlockDetail } from "@/services/home";

function toGenreItem(item: HomeBlockDetailItem): GenreContentItem {
  const ct = (item.content_type || item.type || "movie") as "movie" | "series";
  return {
    uuid: item.uuid,
    unique_code: item.unique_code || item.uuid,
    type: ct,
    content_type: ct,
    title_fa: item.title_fa,
    title_en: item.title_en || "",
    summary: item.summary,
    release_date: item.release_date,
    duration: item.duration,
    status: "Published",
    content_access: item.content_access ?? null,
    coming_soon: false,
    tags: item.tags ?? [],
    imdb_rate: item.imdb_rate ?? item.rating ?? 0,
    age_restriction: null,
    poster_url: item.poster_url,
    banner_url: null,
    category: null,
    country: null,
    total_seasons: item.seasons_count,
    total_episodes: null,
    has_subtitles: item.has_subtitles ?? false,
    has_dubbing: item.has_dubbing ?? false,
    created_at: "",
    updated_at: "",
  };
}

interface Props {
  slug: string;
  initialData: HomeBlockDetailData;
}

export default function BlocksContent({ slug, initialData }: Props) {
  const [items, setItems] = useState<GenreContentItem[]>(
    (initialData.items ?? []).map(toGenreItem)
  );

  const [page, setPage] = useState(initialData.page ?? 1);
  const [totalPages] = useState(initialData.total_pages ?? 1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const hasMore = page < totalPages;

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;
    const nextPage = page + 1;
    if (
      (slug === "newest" || slug === "hottest") &&
      nextPage > 5
    ) {
      return;
    }
    setIsLoadingMore(true);
    try {
      const res = await getHomeBlockDetail({ slug, params: { page: nextPage, limit: initialData.limit ?? 20 } });
      if (res.succeed && res.data?.items) {
        setItems(prev => [...prev, ...res.data.items.map(toGenreItem)]);
        setPage(nextPage);
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, page, slug, initialData.limit]);

  const blockTitle = (initialData.block as { title_fa?: string })?.title_fa || initialData.block?.title || slug;

  return (
    <div className="mx-auto container max-w-3xl pt-25 pb-12 px-6 md:px-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base md:text-lg font-bold text-foreground">{blockTitle}</h2>
      </div>
      <VirtuosoMainCard
        data={items}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />
    </div>
  );
}
